import React, { ChangeEvent, ReactNode, useCallback, useState } from 'react';

import classNames from 'classnames';
import TextField from '@material-ui/core/TextField';
import FormControl from '@material-ui/core/FormControl';
import AddIcon from '@material-ui/icons/Add';

import { Label, Preloader } from '../common';
import { Box } from '../../Box';
import { Button } from '../../Button';

import useStyles from './MultipleTextInput.styles';

interface MultipleTextInputProps {
  id?: string;
  testId?: string;
  type?: string;
  label?: string;
  values: string[];
  placeholder?: string;
  max?: number;
  onChange: (index: number, value: string) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  tooltip?: ReactNode | null;
  error?: boolean;
  helperText?: string;
  required?: boolean;
  disabled?: boolean;
  loading?: boolean;
}

/**
 * Компонент MultipleTextInput представляет собой настраиваемое текстовое поле, которое позволяет вводить несколько текстовых значений.
 * Он поддерживает необязательные метки, всплывающие подсказки, обработку ошибок и состояния загрузки.
 *
 * @component
 * @param {MultipleTextInputProps} props - Свойства для компонента.
 * @param {string} [props.id] - Идентификатор текстовых полей ввода.
 * @param {string} [props.testId] - Необязательный идентификатор для автоматического тестирования.
 * @param {string} [props.type='text'] - Тип текстовых полей ввода (например, 'text', 'email').
 * @param {string} [props.label] - Метка для группы текстовых полей ввода.
 * @param {string[]} [props.values=[]] - Массив текущих значений полей ввода.
 * @param {string} [props.placeholder] - Текст-заполнитель для полей ввода.
 * @param {number} [props.max=3] - Максимальное количество отображаемых полей ввода.
 * @param {(index: number, value: string) => void} props.onChange - Функция обратного вызова, которая вызывается при изменении значения поля ввода.
 * @param {() => void} [props.onFocus=() => {}] - Функция обратного вызова, которая вызывается, когда поле ввода получает фокус.
 * @param {() => void} [props.onBlur=() => {}] - Функция обратного вызова, которая вызывается, когда поле ввода теряет фокус.
 * @param {ReactNode | null} [props.tooltip=null] - Необязательная всплывающая подсказка для отображения с меткой.
 * @param {boolean} [props.error=false] - Наличие ошибки у группы текстовых полей ввода.
 * @param {string} [props.helperText=''] - Вспомогательный текст для отображения под полями ввода.
 * @param {boolean} [props.required=false] - Обязательность группы текстовых полей ввода.
 * @param {boolean} [props.disabled=false] - Отключение группы текстовых полей ввода.
 * @param {boolean} [props.loading=false] - Состояние загрузки группы текстовых полей ввода.
 * @returns {React.ReactElement} Отрендеренный компонент MultipleTextInput.
 */

export const MultipleTextInput = ({
  id,
  testId,
  type = 'text',
  label,
  values = [],
  placeholder,
  max = 3,
  onChange,
  onFocus = () => {},
  onBlur = () => {},
  tooltip,
  error = false,
  helperText = '',
  required = false,
  disabled = false,
  loading = false,
  ...rest
}: MultipleTextInputProps) => {
  const classes = useStyles();

  const [numberOfInputsShown, setNumberOfInputs] = useState(1);

  const counter = Math.max(numberOfInputsShown, values.length);
  const range = new Array(counter).fill(1);

  const handleChange = useCallback(
    (index: number) =>
      (e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) =>
        onChange(index, e.target.value),
    [onChange],
  );

  return (
    <div>
      <FormControl
        data-testid={testId}
        className={classNames(
          classes.multipleTextInput,
          'aqa_multiple_text_input',
        )}
      >
        {label && <Label label={label} required={required} tooltip={tooltip} />}

        {range.map((_, index) => {
          const isButtonShown = index === counter - 1 && index + 1 < max;

          return (
            <Box
              className={classNames(isButtonShown && classes.inputWrapper)}
              mb="16"
              key={index}
              display="flex"
            >
              <Box flexGrow={1}>
                <TextField
                  {...rest}
                  data-testid={`${testId}-index-${index + 1}`}
                  variant="outlined"
                  classes={{ root: classes.input }}
                  className="qaInputWrapper"
                  value={loading ? 'Загрузка...' : values[index] || ''}
                  autoComplete="off"
                  onChange={handleChange(index)}
                  onFocus={onFocus}
                  onBlur={onBlur}
                  fullWidth
                  type={type}
                  error={error}
                  helperText={helperText}
                  placeholder={placeholder || label}
                  disabled={disabled || loading}
                  InputProps={{
                    id: id,
                    className: 'textFieldInput',
                    classes: { adornedEnd: classes.adornedEnd },
                    ...(loading ? { endAdornment: <Preloader /> } : {}),
                  }}
                />
              </Box>

              {isButtonShown && (
                <Box ml="20">
                  <Button
                    size="large"
                    onClick={() => setNumberOfInputs((state) => state + 1)}
                    icon={<AddIcon />}
                    disabled={disabled || loading}
                  />
                </Box>
              )}
            </Box>
          );
        })}
      </FormControl>
    </div>
  );
};
